import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RANK_ORDER } from '@pk/shared';
import type { HouseView, WorldView } from '@pk/shared';
import { memberIds, useCities, useCompanies, useDeliveries, useLoans } from '../api/hooks';
import { Section } from './ui';
import {
  IconBank, IconBanner, IconCart, IconCrown, IconHammer, IconLetter, IconMask, IconPact, IconScroll, IconShield,
  IconStall, IconSword, IconUsers, ResGold,
} from './icons';

type Tile = { key: string; to: string; icon: ReactNode; label: string; status: string; badge?: number; tone?: 'gold' | 'red' };
type Row = { key: string; title: string; tiles: Tile[] };

/**
 * "Your affairs": one big tap target per day-to-day activity, grouped House / Diplomacy / Trade / War & intrigue.
 * Everything comes from data Home already holds (house, world) plus four small cached queries shared with the Economy screen.
 */
export function Affairs({ h, w }: { h: HouseView; w: WorldView }) {
  const { t } = useTranslation();
  const cities = useCities();
  const companies = useCompanies();
  const deliveries = useDeliveries();
  const loans = useLoans();
  const mine = memberIds(h);
  const day = w.day;
  const dash = '…';

  // --- house
  const living = h.members.filter((m) => m.alive);
  const inTraining = living.filter((m) => m.squireXp !== null);
  const unplaced = inTraining.filter((m) => !m.squireOf);
  const knights = living.filter((m) => m.dubbed);
  const chivalryStatus = inTraining.length + knights.length === 0
    ? t('affairs.status.noChivalry')
    : [
      inTraining.length > 0 && t('affairs.status.squires', { count: inTraining.length }),
      unplaced.length > 0 && t('affairs.status.unplaced', { count: unplaced.length }),
      knights.length > 0 && t('affairs.status.knights', { count: knights.length }),
    ].filter(Boolean).join(' · ');
  const held = h.heldTitleIds.map((id) => w.titles.find((x) => x.id === id)).filter((x) => !!x);
  const top = [...held].sort((a, b) => RANK_ORDER[b!.rank] - RANK_ORDER[a!.rank])[0];
  const lands = top ? `/title/${top.id}?tab=council` : '/map';

  // --- diplomacy
  const pendingIn = h.incomingProposals.filter((p) => p.status === 'pending');
  const unread = h.letters.filter((l) => !l.read && mine.has(l.toId) && l.arriveDay <= day).length;
  const mail = pendingIn.length + unread;
  const heldHooks = h.hooks.filter((k) => k.direction === 'held' && k.spent === null).length;
  const owedHooks = h.hooks.filter((k) => k.direction !== 'held' && k.spent === null).length;
  const wardships = [...h.incomingProposals, ...h.outgoingProposals].filter((p) => p.kind === 'wardship' && p.status === 'pending').length;
  const factionsReady = h.factions.filter((f) => f.ready).length;

  // --- trade
  const cityRows = cities.data?.cities;
  const guilded = (cityRows ?? []).filter((c) => (c.myGuildXp ?? 0) > 0)
    .sort((a, b) => (b.myGuildRank ?? 0) - (a.myGuildRank ?? 0) || (b.myGuildXp ?? 0) - (a.myGuildXp ?? 0));
  const bestGuild = guilded[0];
  const myCompanies = companies.data?.companies.filter((c) => mine.has(c.ownerId)).length;
  const caravans = deliveries.data?.deliveries;
  const nextCaravan = caravans && caravans.length > 0 ? Math.max(0, Math.min(...caravans.map((d) => d.arriveDay)) - day) : null;
  const loanRows = loans.data?.loans;
  const overdue = loanRows?.filter((l) => l.role === 'borrower' && (l.missed > 0 || l.defaulted)).length ?? 0;

  // --- war & intrigue
  const activeWars = h.wars.filter((x) => x.endedDay === null).length;
  const plots = h.plotsAgainstUs.length;

  const rows: Row[] = [
    { key: 'house', title: t('affairs.group.house'), tiles: [
      { key: 'family', to: '/?view=family', icon: <IconUsers />, label: t('affairs.family'), status: t('house.members', { count: living.length }) },
      { key: 'chivalry', to: '/?view=family&filter=chivalry', icon: <IconShield />, label: t('affairs.chivalry'), status: chivalryStatus, badge: unplaced.length },
      { key: 'lands', to: lands, icon: <IconCrown />, label: t('affairs.lands'), status: held.length > 0 ? t('affairs.status.titles', { count: held.length }) : t('affairs.status.noLands') },
    ] },
    { key: 'diplomacy', title: t('affairs.group.diplomacy'), tiles: [
      { key: 'mail', to: '/court', icon: <IconLetter />, label: t('affairs.mail'), status: mail > 0 ? [pendingIn.length > 0 && t('affairs.status.toAnswer', { count: pendingIn.length }), unread > 0 && t('affairs.status.unread', { count: unread })].filter(Boolean).join(' · ') : t('affairs.status.allRead'), badge: mail },
      { key: 'pacts', to: '/court?tab=relations', icon: <IconPact />, label: t('affairs.pacts'), status: `${t('affairs.status.allies', { count: h.allyHouseIds.length })}, ${t('affairs.status.pacts', { count: h.napHouseIds.length })}` },
      { key: 'favours', to: '/court?tab=letters', icon: <IconScroll />, label: t('affairs.favours'), status: t('affairs.status.favours', { held: heldHooks, owed: owedHooks, wards: wardships }), badge: wardships },
      { key: 'factions', to: '/court?tab=factions', icon: <IconBanner />, label: t('affairs.factions'), status: h.factions.length > 0 ? t('affairs.status.factions', { count: h.factions.length }) : t('affairs.status.noFactions'), badge: factionsReady },
    ] },
    { key: 'trade', title: t('affairs.group.trade'), tiles: [
      { key: 'markets', to: '/trade', icon: <IconStall />, label: t('affairs.markets'), status: cityRows ? t('affairs.status.cities', { count: cityRows.length }) : dash },
      { key: 'guilds', to: '/trade?view=guilds', icon: <IconHammer />, label: t('affairs.guilds'), status: !cityRows ? dash : bestGuild ? t('affairs.status.bestGuild', { rank: t(`trade.guild.rank.${bestGuild.myGuildRank ?? 0}`), city: bestGuild.name }) : t('affairs.status.noGuild') },
      { key: 'companies', to: '/trade?view=companies', icon: <ResGold />, label: t('affairs.companies'), status: myCompanies == null ? dash : myCompanies > 0 ? t('affairs.status.companies', { count: myCompanies }) : t('affairs.status.noCompanies') },
      { key: 'caravans', to: '/trade?view=caravans', icon: <IconCart />, label: t('affairs.caravans'), status: !caravans ? dash : caravans.length > 0 ? t('affairs.status.caravans', { count: caravans.length, days: nextCaravan ?? 0 }) : t('affairs.status.noCaravans') },
      { key: 'loans', to: '/trade?view=loans', icon: <IconBank />, label: t('affairs.loans'), status: !loanRows ? dash : overdue > 0 ? t('affairs.status.overdue', { count: overdue }) : loanRows.length > 0 ? t('affairs.status.loans', { count: loanRows.length }) : t('affairs.status.noLoans'), badge: overdue, tone: 'red' },
    ] },
    { key: 'war', title: t('affairs.group.war'), tiles: [
      { key: 'armies', to: '/map?view=military', icon: <IconSword />, label: t('affairs.armies'), status: `${t('affairs.status.armies', { count: h.armies.length })}, ${t('affairs.status.wars', { count: activeWars })}`, badge: activeWars, tone: 'red' },
      { key: 'schemes', to: '/court?tab=intrigue', icon: <IconMask />, label: t('affairs.schemes'), status: plots > 0 ? t('affairs.status.plotsAgainst', { count: plots }) : h.schemes.length > 0 ? t('affairs.status.schemes', { count: h.schemes.length }) : t('affairs.status.noSchemes'), badge: plots, tone: 'red' },
    ] },
  ];

  return (
    <Section title={t('affairs.title')} className="affairs">
      {rows.map((r) => (
        <div key={r.key} className="aff-group">
          <h3 className="aff-head">{r.title}</h3>
          <div className="aff-grid">
            {r.tiles.map((x) => (
              <Link key={x.key} to={x.to} className={`aff-tile ${x.tone ?? ''}`} title={`${x.label}: ${x.status}`}>
                <span className="aff-ico" aria-hidden="true">{x.icon}</span>
                <span className="aff-text">
                  <b>{x.label}</b>
                  <span>{x.status}</span>
                </span>
                {x.badge ? <span className="aff-badge num" aria-hidden="true">{x.badge}</span> : null}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </Section>
  );
}
